import data from '../../data/basic.json';
import dataEn from '../../data/en/basic.json';
import settings from '../../data/settings.json';
import { ActionTypes } from '../constants/ActionTypes';

const defaults = {
    units: { ...settings.units }
};

// Sync with DOM data-unit-name
const articleHeaderName = 'article-header';
const articleHeaderUnitName = 'article-header-unit';
const generateHeaderUnitName = (index) => `section-${index}-unit-header`;
const generateUnitName = (sIndex, bIndex) => `section-${sIndex}-unit-${bIndex}`;
const footerName = 'footer';
const footerUnitName = 'article-footer';

const getSectionHeader = (sData, index, stgs) => {
    const {
        theme, previousTheme, scheme, previousScheme, sectionId
    } = stgs;
    const {
        name, title, subtitle, titlePlane, subtitlePlane, objects, subtitleWidthEM, isMajor
    } = sData;

    return {
        name: name || generateHeaderUnitName(index),
        type: 'sectionHeader',
        sectionId,
        index,
        theme,
        previousTheme,
        scheme,
        previousScheme,
        isMajor,
        title,
        subtitle,
        titlePlane,
        subtitlePlane,
        subtitleWidthEM,
        objects,
        unit: {
            weight: stgs.units.sectionHeader?.weight || 1,
            shift: stgs.units.sectionHeader?.shift || 0
        }
    };
};

const getDefaultWeight = (type, unitsStgs) => {
    const stgsByType = unitsStgs[type] ?? { weight: 0.5 };

    return stgsByType.weight;
};

const getSection = (input, sIndex, stgs, prevSectionData) => {
    const {
        name, theme, scheme, isMajor, content
    } = input;
    const previousScheme = prevSectionData?.scheme ?? 'default';
    const previousTheme = prevSectionData?.theme ?? 'default';

    const header = getSectionHeader(input, sIndex, {
        ...stgs,
        theme,
        previousTheme,
        scheme,
        previousScheme,
        isMajor
    });
    const getDefaultWeightFunc = (type) => getDefaultWeight(type, stgs.units);
    let { weight } = header.unit;

    const units = content.map((item, uIndex) => {
        const { unit } = item;
        const unitName = item.name;
        const unitWeight = unit?.weight ?? getDefaultWeightFunc(item.type);

        weight += unitWeight;

        return {
            ...item,
            name: unitName || generateUnitName(sIndex, uIndex),
            theme,
            previousTheme,
            scheme,
            previousScheme,
            unit: {
                weight: unitWeight,
                shift: unit?.shift || 0
            }
        };
    });

    units.unshift(header);

    return {
        name,
        id: name,
        theme,
        previousTheme,
        scheme,
        previousScheme,
        units,
        weight
    };
};

const collectTextures = (input) => {
    const textureKeysAvailable = ['textures'];

    if (!(input instanceof Object)) return [];

    const keys = Object.keys(input);
    const values = Object.values(input);
    let arr = [];

    keys.forEach((key, index) => {
        const value = values[index];

        if (textureKeysAvailable.includes(key)) {
            arr.push({
                textures: value,
                name: input[`${key}Name`]
            });

            return;
        }

        if (value instanceof Object) {
            arr = arr.concat(collectTextures(value));

            return;
        }

        if (value instanceof Array) {
            arr = arr.concat(value.reduce((accumulator, currentValue) => accumulator.concat(collectTextures(currentValue)), []));
        }
    });

    return arr;
};

class Content {
    constructor(lang) {
        const {
            title, titlePlane, subtitle, subtitlePlane, subtitleWidthEM,
            theme, scheme, objects, sections, footer
        } = lang === 'en' ? dataEn : data;

        this.settings = defaults;

        const materialsLibTextures = collectTextures(settings.materialsLib);
        const objectsTextures = collectTextures(objects);
        const sectionsTextures = collectTextures(sections);

        this.texturesCollection = [...materialsLibTextures, ...objectsTextures];
        this.sectionsTexturesCollection = sectionsTextures;

        // Article header - the Cover unit
        const aHeaderUnit = {
            type: 'header',
            name: articleHeaderUnitName,
            theme,
            scheme,
            title,
            subtitle,
            titlePlane,
            subtitlePlane,
            subtitleWidthEM,
            objects,
            unit: {
                weight: this.settings.units.header?.weight || 1,
                shift: this.settings.units.header?.shift || 0
            }
        };

        this.articleHeader = {
            name: articleHeaderName,
            theme,
            scheme,
            units: [aHeaderUnit],
            weight: aHeaderUnit.unit.weight
        };

        // Sections content
        this.sections = sections.map((sInput, index, arr) => getSection(
            sInput, index, this.settings,
            // Previous section
            index > 0 ? arr[index - 1] : null
        ));

        const lastSection = this.sections[this.sections.length - 1];

        // Footer
        const footerUnit = {
            type: 'footer',
            name: footerUnitName,
            theme: lastSection.theme,
            scheme: lastSection.scheme,
            title: footer.title.text,
            titlePrefix: footer.title.prefix,
            link: footer.email.link,
            linkText: footer.email.text,
            clickable: true,
            action: {
                key: ActionTypes.LINK,
                href: footer.email.link
            },
            unit: {
                weight: this.settings.units.footer?.weight || 1,
                shift: this.settings.units.footer?.shift || 0
            }
        };

        this.footer = {
            name: footerName,
            id: footer.name,
            theme: footerUnit.theme,
            scheme: footerUnit.scheme,
            units: [footerUnit],
            weight: footerUnit.unit.weight
        };
    }

    get totalWeight() {
        return this.articleHeader.weight + this.footer.weight
            + [0, ...this.sections].reduce((accumulate, section) => accumulate + section.weight);
    }

    get scrollWeight() {
        return this.totalWeight > 1 ? (this.totalWeight - 1) : 0;
    }
}

let instance;

export const contentStoreInstance = (lang) => {
    if (instance) return instance;

    instance = new Content(lang);

    return instance;
};
